import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../../context/ChatContext';
import { useBilling } from '../../context/BillingContext';
import { config } from '../../config';
import { MessageCircle, X, Send, Bot, User, Minimize2, Maximize2 } from 'lucide-react';

interface FloatingChatWidgetProps {
    currentPageTitle: string;
}

const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({ currentPageTitle }) => {
    const { conversations, activeConvId, setActiveConvId, sendMessage, isPolling, isWidgetOpen, setIsWidgetOpen, streamingMessage, suggestions, setSuggestions } = useChat();
    const { user } = useBilling();
    const [input, setInput] = useState('');
    const [activeTab, setActiveTab] = useState('ai_thread');
    const [programContext, setProgramContext] = useState<string | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);

    const activeThread = conversations?.[activeTab] || { messages: [] };

    // Load program context for AI RAG on mount
    useEffect(() => {
        if (user?.email) {
            fetch(`${config.API_BASE_URL}/api/biz-builder/execute`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ task: 'get_program_context', data: { userEmail: user.email } }),
            })
                .then(r => r.json())
                .then(data => {
                    if (data.success && data.result) {
                        const ctx = data.result;
                        const lessonsText = (ctx.lessons || []).map((l: any) =>
                            `Lesson ${l.id}: ${l.title}\n${l.content || ''}`
                        ).join('\n\n');
                        const resourcesText = (ctx.resources || []).map((r: any) =>
                            `Resource: ${r.title} (${r.type || 'Document'})${r.lessonId ? ` — Lesson ${r.lessonId}` : ''}`
                        ).join('\n');
                        setProgramContext(`You are a helpful program assistant. Use the following program content to answer user questions:\n\n=== LESSONS ===\n${lessonsText}\n\n=== RESOURCES ===\n${resourcesText}`);
                    }
                })
                .catch(err => console.error('Failed to load program context:', err));
        }
    }, [user?.email]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isWidgetOpen) {scrollToBottom();}
    }, [activeThread.messages, isWidgetOpen, streamingMessage]);

    const handleSendWithText = async (text: string) => {
        if (!text.trim()) {return;}
        const context: any = {
            url: window.location.href,
            title: document.title,
            label: currentPageTitle || "Business OS",
        };
        if (activeTab === 'ai_thread' && programContext) {
            context.programContext = programContext;
        }
        await sendMessage(activeTab, text, context);
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim()) {return;}

        const context: any = {
            url: window.location.href,
            title: document.title,
            label: currentPageTitle || "Business OS",
        };

        // Inject program context for AI Agent tab
        if (activeTab === 'ai_thread' && programContext) {
            context.programContext = programContext;
        }

        await sendMessage(activeTab, input, context);
        setInput('');
    };

    if (!isWidgetOpen) {
        return (
            <button
                onClick={() => setIsWidgetOpen(true)}
                className="fixed bottom-24 right-6 z-50 bg-gradient-to-tr from-blue-600 to-indigo-900 text-white p-5 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all animate-in fade-in slide-in-from-bottom-10 border-2 border-white/20"
            >
                <div className="relative">
                    <MessageCircle size={32} strokeWidth={2.5} />
                    {/* Unread Badge restored */}
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                </div>
            </button>
        );
    }

    return (
        <div className="fixed bottom-24 right-6 z-50 w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] animate-in slide-in-from-bottom-10 fade-in duration-300">

            {/* Header */}
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
                <div>
                    <h3 className="font-bold text-lg">Business Assistant</h3>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                        {isPolling ? <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" /> : <span className="w-2 h-2 bg-slate-500 rounded-full" />}
                        {isPolling ? 'Syncing...' : 'Online'} • {currentPageTitle || 'General'}
                    </p>
                </div>
                <button onClick={() => setIsWidgetOpen(false)} className="text-slate-400 hover:text-white">
                    <X size={20} />
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-100 shrink-0">
                <button
                    onClick={() => setActiveTab('ai_thread')}
                    className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${activeTab === 'ai_thread' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Bot size={16} />
                    AI Agent
                </button>
                <button
                    onClick={() => setActiveTab('coach_thread')}
                    className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${activeTab === 'coach_thread' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <User size={16} />
                    My Coach
                </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
                {activeThread.messages.length === 0 && (
                    <div className="text-center text-slate-400 text-sm mt-10">
                        <p>No messages yet.</p>
                        <p className="text-xs mt-1">Start a conversation!</p>
                    </div>
                )}

                {activeThread.messages.map((msg) => {
                    const isMe = msg.sender !== 'AI_AGENT' && msg.sender !== 'Coach'; // Simple check, refine with email
                    // Actually, sender is email. 'AI_AGENT' is specific.
                    // For AI Thread: Me vs AI_AGENT
                    // For Coach Thread: Me vs Coach Email

                    const isAi = msg.sender === 'AI_AGENT';
                    const isCoach = !isAi && msg.sender !== 'me' && !msg.sender.includes('@'); // Placeholder logic, needs real email check

                    // Better Logic:
                    // If thread is AI:
                    //   isMe = msg.sender !== 'AI_AGENT'
                    // If thread is Coach:
                    //   isMe = msg.sender === 'me' || msg.sender === userEmail (passed in context?)

                    // Let's assume 'AI_AGENT' and specific Coach Email. 
                    // Any generic 'sender' that isn't AI_AGENT is likely the user effectively in this local view context 
                    // (since we don't have 'me' hardcoded, we rely on email).
                    // VISUAL HACK: If sender == 'AI_AGENT' -> Left. Else -> Right.

                    const alignRight = msg.sender !== 'AI_AGENT' && msg.sender !== 'COACH';

                    return (
                        <div key={msg.messageId} className={`flex ${alignRight ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl p-3 text-sm shadow-sm ${alignRight
                                ? 'bg-blue-600 text-white rounded-tr-none'
                                : 'bg-white text-slate-700 border border-slate-200 rounded-tl-none'
                                }`}>
                                <div className="break-words">{msg.content}</div>
                                <div className={`text-[10px] mt-1 text-right ${alignRight ? 'text-blue-200' : 'text-slate-400'}`}>
                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {/* Streaming message from AI */}
                {streamingMessage && (
                    <div className="flex justify-start">
                        <div className="max-w-[85%] rounded-2xl p-3 text-sm shadow-sm bg-white text-slate-700 border border-slate-200 rounded-tl-none">
                            {streamingMessage.isStreaming && !streamingMessage.content && (
                                <div className="flex items-center gap-2 text-slate-400">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    <span className="text-xs ml-1">Thinking...</span>
                                </div>
                            )}
                            {streamingMessage.content && (
                                <div>
                                    <div className="break-words whitespace-pre-wrap">{streamingMessage.content}</div>
                                    {streamingMessage.isStreaming && (
                                        <span className="inline-block w-1.5 h-4 bg-blue-500 ml-0.5 animate-pulse" />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Follow-up suggestion chips */}
                {suggestions.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                        {suggestions.map((suggestion, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    setSuggestions([]);
                                    handleSendWithText(suggestion);
                                }}
                                className="text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-3 py-1.5 hover:bg-blue-100 active:scale-95 transition-all"
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-3 bg-white border-t border-slate-100 shrink-0">
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={`Message ${activeTab === 'ai_thread' ? 'AI' : 'Coach'}...`}
                        className="flex-1 bg-transparent border-none outline-none text-sm placeholder:text-slate-400"
                    />
                    <button
                        type="submit"
                        disabled={!input.trim()}
                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                    >
                        <Send size={16} />
                    </button>
                </div>
            </form>
        </div>
    );
};

export default FloatingChatWidget;
